import { useState } from "react";
import { X, Monitor, FlaskConical } from "lucide-react";

const STORAGE_KEY = "environment-banner-dismissed";

type Environment = "development" | "staging" | "production";

function getEnvironment(): Environment {
  const explicit = import.meta.env.VITE_APP_ENVIRONMENT?.toLowerCase();
  if (explicit === "development" || explicit === "staging" || explicit === "production") {
    return explicit;
  }
  // Fallback: Vite's built-in mode
  return import.meta.env.MODE === "development" ? "development" : "production";
}

const config: Record<Exclude<Environment, "production">, {
  label: string;
  className: string;
  icon: typeof Monitor;
}> = {
  development: {
    label: "Development Environment",
    className: "bg-blue-600 text-white",
    icon: Monitor,
  },
  staging: {
    label: "Staging Environment",
    className: "bg-amber-500 text-white",
    icon: FlaskConical,
  },
};

export function EnvironmentBanner() {
  const environment = getEnvironment();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(STORAGE_KEY) === "true"
  );

  if (environment === "production" || dismissed) {
    return null;
  }

  const { label, className, icon: Icon } = config[environment];

  const handleDismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, "true");
    setDismissed(true);
  };

  return (
    <div className={`${className} px-4 py-1 flex items-center justify-center relative text-sm`}>
      <Icon className="h-3.5 w-3.5 mr-1.5" />
      <span>{label}</span>
      <button
        onClick={handleDismiss}
        className="absolute right-3 p-0.5 rounded hover:bg-white/20 transition-colors"
        aria-label="Dismiss environment banner"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
